import React from "react";
import { withStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import IconButton from "@material-ui/core/IconButton";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import { Grid, TextField } from "@material-ui/core";
import {
  makeSpotifyAPIRequest,
  makePlayerRequest,
  updatePlaylistsForSong,
} from "../api_utilities/spotify";
import {
  makeBackendAPIRequest,
  backendBaseURL,
} from "../api_utilities/backend";
import Snackbar from "@material-ui/core/Snackbar";
import TagDialog from "./TagDialog";
import ListItem from "@material-ui/core/ListItem";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ListItemText from "@material-ui/core/ListItemText";
import { observer } from "mobx-react";

const useStyles = (theme) => ({
  root: {
    width: "100%",
    marginBottom: theme.spacing(1),
  },
  inputRounded: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 50,
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
});

const Track = observer(
  class Track extends React.Component {
    constructor(props) {
      super(props);

      this.state = {
        trackRatingError: false,
        isSnackbarOpen: false,
        snackbarMessage: "",
        snackbarError: false,
        isTagDialogOpen: false,
      };
    }

    pauseTrack = () => {
      const { store } = this.props;
      const url = new URL("https://api.spotify.com/v1/me/player/pause");
      makePlayerRequest(url, "PUT", store.updateCurrentlyPlayingTrack);
    };

    playTrack = (_, deviceID) => {
      const { trackAndAlbumInfo, trackIDsAfter, store } = this.props;

      const url = new URL("https://api.spotify.com/v1/me/player/play");
      if (deviceID != null) {
        url.searchParams.append("device_id", deviceID);
      }
      let body = {};
      if (store.currentlyPlayingTrack !== trackAndAlbumInfo.trackID) {
        body = {
          uris: ["spotify:track:" + trackAndAlbumInfo.trackID].concat(
            trackIDsAfter.map((trackID) => "spotify:track:" + trackID)
          ),
        };
      }
      makeSpotifyAPIRequest({
        url: url,
        method: "PUT",
        body: body,
        onSuccessCallback: store.updateCurrentlyPlayingTrack,
        onNoContentCallback: () => {},
        onFailureCallback: (data) => {
          if (data.error.status !== 404 || deviceID != null) {
            return;
          }

          const deviceUrl = new URL(
            "https://api.spotify.com/v1/me/player/devices"
          );
          makeSpotifyAPIRequest({
            url: deviceUrl,
            method: "GET",
            onSuccessCallback: (data) => {
              if (data.devices.length > 0) {
                this.playTrack(null, data.devices[0].id);
              } else {
                this.setState({
                  isSnackbarOpen: true,
                  snackbarError: true,
                  snackbarMessage:
                    "No active devices found: play a track on spotify directly then try again.",
                });
              }
            },
            onNoContentCallback: () => {},
            onFailureCallback: () => {},
          });
        },
      });
    };

    rateTrack = (event) => {
      const newValue = event.target.value;

      if (newValue !== "-" && isNaN(newValue)) {
        this.setState({ trackRatingError: true });
        return;
      }

      const { trackAndAlbumInfo, onTrackRated, store } = this.props;

      if (store.currentlyPlayingTrack.trackID === trackAndAlbumInfo.trackID) {
        store.rateCurrentlyPlayingTrack(newValue);
      } else {
        store.rateTrack(trackAndAlbumInfo.trackID, newValue);
      }

      onTrackRated.optimistic(
        trackAndAlbumInfo.albumIndex,
        trackAndAlbumInfo.trackIndex,
        newValue
      );

      const url = new URL(backendBaseURL + "/song/rate_song");
      makeBackendAPIRequest({
        url: url,
        method: "POST",
        body: {
          id_song_spotify: trackAndAlbumInfo.trackID,
          id_album_spotify: trackAndAlbumInfo.albumID,
          album_total_tracks: trackAndAlbumInfo.albumTotalTracks,
          rating: newValue,
          album_name: trackAndAlbumInfo.albumName,
          release_year: trackAndAlbumInfo.releaseYear,
          album_art_url: trackAndAlbumInfo.albumArtURL,
          album_artists: trackAndAlbumInfo.albumArtists,
          song_name: trackAndAlbumInfo.trackName,
          duration_ms: trackAndAlbumInfo.trackLength,
          track_number: trackAndAlbumInfo.trackNumber,
        },
        onSuccessCallback: (data) => {
          this.setState({
            trackRatingError: false,
            snackbarMessage: "Track rating saved",
            isSnackbarOpen: true,
            snackbarError: false,
          });

          updatePlaylistsForSong(
            trackAndAlbumInfo.trackID,
            data.add_to_playlists,
            data.remove_from_playlists
          );

          onTrackRated.callback(
            trackAndAlbumInfo.albumID,
            trackAndAlbumInfo.albumIndex,
            newValue
          );
        },
        onFailureCallback: () => {
          this.setState({
            snackbarError: true,
            isSnackbarOpen: true,
            snackbarMessage: "Failed to save track rating",
          });

          onTrackRated.optimistic(
            trackAndAlbumInfo.albumIndex,
            trackAndAlbumInfo.trackIndex,
            trackAndAlbumInfo.rating
          );

          if (
            store.currentlyPlayingTrack.trackID === trackAndAlbumInfo.trackID
          ) {
            store.rateCurrentlyPlayingTrack(trackAndAlbumInfo.rating);
          }
        },
      });
    };

    onClickAddTagsButton = () => {
      this.setState({ isTagDialogOpen: true });
    };

    tagDialogOnClose = () => {
      this.setState({ isTagDialogOpen: false });
    };

    render() {
      const {
        classes,
        tabIndex,
        trackAndAlbumInfo,
        onTagsUpdatedOptimistic,
        store,
      } = this.props;
      const {
        trackRatingError,
        isSnackbarOpen,
        snackbarError,
        snackbarMessage,
        isTagDialogOpen,
      } = this.state;

      let tags, rating;

      if (store.currentlyPlayingTrack.trackID === trackAndAlbumInfo.trackID) {
        tags = store.currentlyPlayingTrack.tags;
      } else if (
        trackAndAlbumInfo.trackID in store.ratedOrTaggedTracks &&
        store.ratedOrTaggedTracks[trackAndAlbumInfo.trackID]["tags"] != null
      ) {
        tags = store.ratedOrTaggedTracks[trackAndAlbumInfo.trackID]["tags"];
      } else {
        tags = trackAndAlbumInfo.tags;
      }

      if (store.currentlyPlayingTrack.trackID === trackAndAlbumInfo.trackID) {
        rating = store.currentlyPlayingTrack.rating;
      } else if (
        trackAndAlbumInfo.trackID in store.ratedOrTaggedTracks &&
        store.ratedOrTaggedTracks[trackAndAlbumInfo.trackID]["rating"] != null
      ) {
        rating = store.ratedOrTaggedTracks[trackAndAlbumInfo.trackID]["rating"];
      } else {
        rating = trackAndAlbumInfo.rating;
      }

      let displayTrackName = trackAndAlbumInfo.trackName;
      let displayArtistName = trackAndAlbumInfo.artistName;

      if (displayTrackName.length > 60) {
        displayTrackName = displayTrackName.substring(0, 60) + "...";
      }

      if (displayArtistName.length > 60) {
        displayArtistName = displayArtistName.substring(0, 60) + "...";
      }

      return (
        <div>
          <ListItem className={classes.root}>
            <ListItemText
              primary={displayTrackName}
              secondary={displayArtistName}
            />
            <ListItemSecondaryAction>
              <Grid container spacing={2}>
                <Grid item xs={6} />
                <Grid item container xs={2} justify="center" direction="column">
                  {store.currentlyPlayingTrack.trackID ===
                    trackAndAlbumInfo.trackID && store.isCurrentlyPlaying ? (
                    <IconButton onClick={this.pauseTrack}>
                      <PauseIcon />
                    </IconButton>
                  ) : (
                    <IconButton onClick={this.playTrack}>
                      <PlayArrowIcon />
                    </IconButton>
                  )}
                </Grid>
                <Grid item container xs={2} justify="center" direction="column">
                  <TextField
                    error={trackRatingError}
                    variant="outlined"
                    size="small"
                    className={classes.inputRounded}
                    onChange={this.rateTrack}
                    value={rating}
                    inputProps={{
                      style: { textAlign: "center" },
                      tabIndex: tabIndex ?? 0,
                    }}
                  />
                </Grid>
                <Grid item container xs={2} justify="center" direction="column">
                  <IconButton
                    onClick={this.onClickAddTagsButton}
                    edge="end"
                    tabIndex={tabIndex ?? 0}
                  >
                    {tags.length > 0 ? (
                      <LocalOfferIcon color="primary" />
                    ) : (
                      <LocalOfferOutlinedIcon color="primary" />
                    )}
                  </IconButton>
                </Grid>
              </Grid>
            </ListItemSecondaryAction>
          </ListItem>
          <Snackbar
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            open={isSnackbarOpen}
            autoHideDuration={snackbarMessage.length * 150}
            onClose={() => {
              this.setState({ isSnackbarOpen: false });
            }}
          >
            {snackbarError ? (
              <Alert
                onClose={() => {
                  this.setState({ isSnackbarOpen: false });
                }}
                severity="error"
              >
                {snackbarMessage}
              </Alert>
            ) : (
              <Alert
                onClose={() => {
                  this.setState({ isSnackbarOpen: false });
                }}
                severity="success"
              >
                {snackbarMessage}
              </Alert>
            )}
          </Snackbar>
          <TagDialog
            trackAndAlbumInfo={{
              albumArtists: trackAndAlbumInfo.albumArtists,
              albumArtURL: trackAndAlbumInfo.albumArtURL,
              albumIndex: trackAndAlbumInfo.albumIndex,
              albumID: trackAndAlbumInfo.albumID,
              albumName: trackAndAlbumInfo.albumName,
              albumTotalTracks: trackAndAlbumInfo.albumTotalTracks,
              releaseYear: trackAndAlbumInfo.releaseYear,
              tags: tags,
              trackID: trackAndAlbumInfo.trackID,
              trackIndex: trackAndAlbumInfo.trackIndex,
              trackName: trackAndAlbumInfo.trackName,
              trackLength: trackAndAlbumInfo.trackLength,
              trackNumber: trackAndAlbumInfo.trackNumber,
            }}
            isOpen={isTagDialogOpen}
            onClose={this.tagDialogOnClose}
            onTagsUpdatedOptimistic={onTagsUpdatedOptimistic}
            store={store}
          />
        </div>
      );
    }
  }
);

export default withStyles(useStyles)(Track);
